import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowTask,
  WorkflowDecision,
  WorkflowTaskSearchCriteria
} from '../../models/file-vault.models';

export interface TaskAction {
  action: 'approve' | 'reject' | 'delegate' | 'skip' | 'view';
  task: WorkflowTask;
}

@Component({
  selector: 'fv-my-tasks',
  standalone: true,
  template: `
    <div class="my-tasks-container">
      <!-- Header -->
      <div class="header">
        <div class="header-left">
          <h2 class="title">My Tasks</h2>
          <span class="task-count" *ngIf="!isLoading">
            {{ gridData.total }} pending {{ gridData.total === 1 ? 'task' : 'tasks' }}
          </span>
        </div>
        <div class="header-right">
          <label class="filter-label">
            <input type="checkbox" [(ngModel)]="showOverdueOnly" (change)="onFilterChange()" />
            Overdue only
          </label>
          <kendo-dropdownlist
            [(ngModel)]="filterEntityType"
            [data]="entityTypes"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onFilterChange()"
            class="filter-dropdown">
          </kendo-dropdownlist>
          <button kendoButton [look]="'flat'" (click)="refresh()">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <span class="loading-spinner"></span>
        Loading tasks...
      </div>

      <!-- Grid -->
      <kendo-grid
        *ngIf="!isLoading"
        [data]="gridData"
        [pageSize]="pageSize"
        [skip]="skip"
        [pageable]="pageable"
        [sortable]="true"
        [sort]="sort"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChange($event)"
        [selectable]="{ mode: 'single' }"
        (selectionChange)="onSelectionChange($event)"
        class="tasks-grid">

        <kendo-grid-column field="workflowName" title="Workflow" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="workflow-name">{{ dataItem.workflowName }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="stepName" title="Step" [width]="150">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="step-info">
              <span class="step-name">{{ dataItem.stepName }}</span>
              <span class="step-progress">({{ dataItem.stepIndex + 1 }}/{{ dataItem.totalSteps }})</span>
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="entityType" title="Entity" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="entity-type-badge" [ngClass]="getEntityTypeBadgeClass(dataItem.entityType)">
              {{ dataItem.entityType }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="entityNumber" title="ID/Number" [width]="140">
          <ng-template kendoGridCellTemplate let-dataItem>
            <a class="entity-link" (click)="onViewEntityClick(dataItem)">
              {{ dataItem.entityNumber || dataItem.entityId | slice:0:8 }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="entityTitle" title="Title" [width]="200">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="entity-title" [title]="dataItem.entityTitle">
              {{ dataItem.entityTitle || '-' }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="assignedToRole" title="Role" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="role-badge" *ngIf="dataItem.assignedToRole">
              {{ dataItem.assignedToRole }}
            </span>
            <span *ngIf="!dataItem.assignedToRole">Direct</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="dueDate" title="Due" [width]="110">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span [ngClass]="{ 'overdue': dataItem.isOverdue, 'due-soon': isDueSoon(dataItem) }">
              {{ dataItem.dueDate ? (dataItem.dueDate | date:'shortDate') : 'No due date' }}
            </span>
            <span class="overdue-indicator" *ngIf="dataItem.isOverdue" title="Overdue">!</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Actions" [width]="160" *ngIf="showActions">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <button kendoButton
                      fillMode="flat"
                      (click)="onApproveClick(dataItem)"
                      *ngIf="canApprove(dataItem)"
                      title="Approve"
                      aria-label="Approve"
                      class="btn-success">
                <kendo-icon name="check"></kendo-icon>
              </button>
              <button kendoButton
                      fillMode="flat"
                      (click)="onRejectClick(dataItem)"
                      *ngIf="canReject(dataItem)"
                      title="Reject"
                      aria-label="Reject"
                      class="btn-danger">
                <kendo-icon name="cancel"></kendo-icon>
              </button>
              <button kendoButton
                      fillMode="flat"
                      (click)="onDelegateClick(dataItem)"
                      title="Delegate"
                      aria-label="Delegate">
                <kendo-icon name="user"></kendo-icon>
              </button>
              <button kendoButton
                      fillMode="flat"
                      (click)="onSkipClick(dataItem)"
                      *ngIf="dataItem.canSkip"
                      title="Skip"
                      aria-label="Skip">
                <kendo-icon name="arrow-right"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!isLoading && gridData.total === 0">
        <div class="empty-icon">&#10004;</div>
        <h3>All caught up!</h3>
        <p>You have no pending approval tasks.</p>
      </div>
    </div>
  `,
  styles: [`
    .my-tasks-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
      flex-wrap: wrap;
      gap: 12px;
    }

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 12px;
    }

    .title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .task-count {
      font-size: 14px;
      color: var(--fv-text-secondary);
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .filter-label {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      cursor: pointer;
    }

    .filter-dropdown {
      width: 140px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .loading-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .tasks-grid {
      flex: 1;
      font-size: 13px;
    }

    .workflow-name {
      font-weight: 500;
    }

    .step-info {
      display: flex;
      align-items: baseline;
      gap: 4px;
    }

    .step-name {
      color: var(--fv-text);
    }

    .step-progress {
      font-size: 11px;
      color: var(--fv-text-muted);
    }

    .entity-type-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .entity-type-badge.change { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .entity-type-badge.part { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .entity-type-badge.document { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .entity-type-badge.partversion { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .entity-type-badge.documentversion { background: var(--fv-status-error-bg); color: #c2185b; }

    .entity-link {
      color: var(--fv-text-accent);
      cursor: pointer;
      font-family: monospace;
    }

    .entity-link:hover {
      text-decoration: underline;
    }

    .entity-title {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 180px;
    }

    .role-badge {
      display: inline-block;
      padding: 2px 6px;
      background: var(--fv-status-bluegrey-bg);
      border-radius: 4px;
      font-size: 11px;
      color: var(--fv-status-bluegrey-fg);
    }

    .overdue {
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }

    .due-soon {
      color: var(--fv-status-warning-fg);
    }

    .overdue-indicator {
      display: inline-block;
      margin-left: 4px;
      width: 16px;
      height: 16px;
      background: var(--fv-status-error-fg);
      color: white;
      border-radius: 50%;
      font-size: 10px;
      font-weight: bold;
      text-align: center;
      line-height: 16px;
    }

    .action-buttons {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
    }

    .btn-success { color: #28a745; }
    .btn-danger { color: #dc3545; }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 64px;
      color: var(--fv-text-secondary);
    }

    .empty-icon {
      font-size: 48px;
      color: #4caf50;
      margin-bottom: 16px;
    }

    .empty-state h3 {
      margin: 0 0 8px 0;
      color: var(--fv-text);
    }

    .empty-state p {
      margin: 0;
      color: var(--fv-text-muted);
    }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, DropDownsModule, InputsModule, IconsModule, KENDO_ICONS]
})
export class MyTasksComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Show action buttons in grid */
  @Input() showActions = true;

  /** User name for "my tasks" queries */
  @Input() userName = 'system';

  /** Enable pagination */
  @Input() pageable = true;

  /** Pre-filter by entity type */
  @Input() filterByEntityType?: string;

  /** Emitted when an action is performed */
  @Output() actionClick = new EventEmitter<TaskAction>();

  /** Emitted when a task is selected */
  @Output() selectionChange = new EventEmitter<WorkflowTask | null>();

  /** Emitted when view entity is clicked */
  @Output() viewEntityClick = new EventEmitter<WorkflowTask>();

  gridData: GridDataResult = { data: [], total: 0 };
  isLoading = false;
  pageSize = 20;
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'dueDate', dir: 'asc' }];

  showOverdueOnly = false;
  filterEntityType: string | null = null;

  entityTypes = [
    { text: 'All Types', value: null },
    { text: 'Change', value: 'Change' },
    { text: 'Part', value: 'PartVersion' },
    { text: 'Document', value: 'DocumentVersion' }
  ];

  ngOnInit(): void {
    if (this.filterByEntityType) {
      this.filterEntityType = this.filterByEntityType;
    }
    this.loadTasks();
  }

  async loadTasks(): Promise<void> {
    this.isLoading = true;

    try {
      // For simple use cases, use getMyTasks
      // For filtered/paginated, would use searchTasks
      let tasks: WorkflowTask[];

      if (this.showOverdueOnly) {
        tasks = await this.api.getOverdueTasks().toPromise() ?? [];
      } else {
        tasks = await this.api.getMyTasks(this.userName).toPromise() ?? [];
      }

      // Client-side filtering by entity type
      if (this.filterEntityType) {
        tasks = tasks.filter(t => t.entityType === this.filterEntityType);
      }

      // Client-side sorting
      const sortField = this.sort[0]?.field;
      const sortDir = this.sort[0]?.dir;
      if (sortField) {
        tasks = [...tasks].sort((a: any, b: any) => {
          const aVal = a[sortField];
          const bVal = b[sortField];
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return sortDir === 'asc' ? 1 : -1;
          if (bVal == null) return sortDir === 'asc' ? -1 : 1;
          const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }

      // Client-side pagination
      const total = tasks.length;
      const pagedTasks = this.pageable
        ? tasks.slice(this.skip, this.skip + this.pageSize)
        : tasks;

      this.gridData = {
        data: pagedTasks,
        total
      };
    } catch (error) {
      console.error('Failed to load tasks', error);
      this.gridData = { data: [], total: 0 };
    } finally {
      this.isLoading = false;
    }
  }

  onFilterChange(): void {
    this.skip = 0;
    this.loadTasks();
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.loadTasks();
  }

  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.loadTasks();
  }

  onSelectionChange(event: any): void {
    const selected = event.selectedRows?.[0]?.dataItem;
    this.selectionChange.emit(selected ?? null);
  }

  onApproveClick(task: WorkflowTask): void {
    this.actionClick.emit({ action: 'approve', task });
  }

  onRejectClick(task: WorkflowTask): void {
    this.actionClick.emit({ action: 'reject', task });
  }

  onDelegateClick(task: WorkflowTask): void {
    this.actionClick.emit({ action: 'delegate', task });
  }

  onSkipClick(task: WorkflowTask): void {
    this.actionClick.emit({ action: 'skip', task });
  }

  onViewEntityClick(task: WorkflowTask): void {
    this.viewEntityClick.emit(task);
  }

  refresh(): void {
    this.loadTasks();
  }

  canApprove(task: WorkflowTask): boolean {
    return task.allowedDecisions?.includes(WorkflowDecision.Approve) ?? true;
  }

  canReject(task: WorkflowTask): boolean {
    return task.allowedDecisions?.includes(WorkflowDecision.Reject) ?? true;
  }

  isDueSoon(task: WorkflowTask): boolean {
    if (!task.dueDate || task.isOverdue) return false;
    const dueDate = new Date(task.dueDate);
    const now = new Date();
    const diffDays = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 2;
  }

  getEntityTypeBadgeClass(entityType: string): string {
    return entityType?.toLowerCase() ?? '';
  }
}
